export default function GeneticsSection() {
  return (
    <section id="genetics" className="py-16 bg-white">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 flex items-center justify-center bg-brand-50 rounded-xl">
            <i className="ri-test-tube-line text-brand-500 text-xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 font-heading">Genetic Basis</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-5">
            <p className="text-gray-600 leading-relaxed font-body">
              Cardiospondylocarpofacial syndrome (CSCFS; OMIM #157800) is caused by heterozygous pathogenic variants in the <strong className="text-gray-800">MAP3K7</strong> gene, located on chromosome 6q15. The gene encodes <strong className="text-gray-800">TGF-β-activated kinase 1 (TAK1)</strong>, a serine-threonine protein kinase that is widely expressed and participates in multiple molecular and cellular processes.
            </p>
            <p className="text-gray-600 leading-relaxed font-body">
              TAK1 forms a complex with specific binding proteins and modulates downstream effectors including c-Jun amino-terminal kinases (JNKs), extracellular-signal regulated kinases (ERKs), p38 MAP kinase, and nuclear factor-κB — affecting a wide range of cellular processes including cell growth and differentiation, immune function, stress responses, and apoptosis.
            </p>
            <p className="text-gray-600 leading-relaxed font-body">
              The nearly ubiquitous expression of TAK1 and the early lethality of knockout mice speak to the critical importance of this protein in embryonic development. In CSCFS, MAP3K7 variants have been shown to <strong className="text-gray-800">decrease TAK1 autophosphorylation</strong> and disrupt downstream TAK1-dependent signaling pathways — a key distinction from the related condition Frontometaphyseal Dysplasia Type 2 (FMD2), where variants cause <em>elevated</em> phosphorylation.
            </p>
            <p className="text-gray-600 leading-relaxed font-body">
              CSCFS follows an <strong className="text-gray-800">autosomal dominant inheritance pattern</strong>. All known variants are heterozygous and de novo in the majority of reported cases. To date, all small in-frame deletions and splice variants have been associated exclusively with CSCFS, whereas missense variants can cause either CSCFS or FMD2 depending on their specific location and effect on kinase activity.
            </p>
          </div>

          <div className="space-y-4">
            <div className="bg-brand-50 border border-brand-100 rounded-2xl p-5">
              <p className="text-xs font-semibold text-brand-600 uppercase tracking-wider mb-3 font-heading">Key Gene Facts</p>
              <ul className="space-y-3 text-sm text-gray-700 font-body">
                {[
                  { label: 'Gene', value: 'MAP3K7' },
                  { label: 'Chromosome', value: '6q15' },
                  { label: 'Protein', value: 'TAK1 (TGF-β-activated kinase 1)' },
                  { label: 'Inheritance', value: 'Autosomal dominant' },
                  { label: 'OMIM', value: '#157800' },
                  { label: 'TAK1 effect', value: 'Decreased autophosphorylation' },
                ].map((item) => (
                  <li key={item.label} className="flex items-start gap-2">
                    <i className="ri-map-pin-line text-brand-400 mt-0.5 flex-shrink-0"></i>
                    <span><strong>{item.label}:</strong> {item.value}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-accent-50 border border-accent-100 rounded-2xl p-5">
              <p className="text-xs font-semibold text-accent-600 uppercase tracking-wider mb-2 font-heading">Related Condition</p>
              <p className="text-sm text-gray-600 leading-relaxed font-body">
                <strong>Frontometaphyseal Dysplasia 2 (FMD2)</strong> is caused by different variants in the same MAP3K7 gene, but causes <em>elevated</em> TAK1 phosphorylation. The two conditions share some features but can be clinically distinguished.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 text-xs text-gray-400 border-t border-gray-100 pt-4 font-body">
          Sources: Le Goff et al., <em>Am J Hum Genet</em> 2016; AbuBakr et al., <em>Cold Spring Harb Mol Case Stud</em> 2020; Nyuzuki et al., <em>Hum Genome Var</em> 2024
        </div>
      </div>
    </section>
  );
}
